import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { catchError, filter, map, switchMap } from "rxjs/operators";
import { of } from "rxjs";

import {
  IGroup,
  LoadingStatus,
  NavigationLevel,
} from "../../../../../types/NendaTypes";
import { CustomerPortalEpic, CustomerPortalState } from "..";
import { companyService } from "@client/http/companyService";
import { SetNotificationError } from "../reducers/notificationReducer";
import { GroupProps } from "@nendaTypes/RequestTypes";
import { organizationUnitService } from "@client/http/organizationUnit.service";
import {
  NavigationScope,
  selectCurrentNavigationLevel,
  selectNavigationScope,
} from "./workspaceReducer";

export interface CreateGroupPayload {
  companyOrPremiseId: string;
  group: GroupProps;
}

export interface UpdateGroupPayload {
  companyOrPremiseId: string;
  id: string;
  changes: Partial<GroupProps>;
}

export interface DeleteGroupPayload {
  companyOrPremiseId: string;
  id: string;
}

export interface GroupState {
  companyGroups: IGroup[];
  premiseGroups: IGroup[];
  selectedGroup?: IGroup;
  loadingStatus: LoadingStatus;
}

export const initialState: GroupState = {
  companyGroups: [],
  premiseGroups: [],
  loadingStatus: LoadingStatus.IDLE,
};

const setLoading = (state: GroupState) => {
  state.loadingStatus = LoadingStatus.LOADING;
};
const setSucceeded = (state: GroupState) => {
  state.loadingStatus = LoadingStatus.SUCCEEDED;
};
const setFailed = (state: GroupState) => {
  state.loadingStatus = LoadingStatus.FAILED;
};

const groupSlice = createSlice({
  name: "group",
  initialState,
  reducers: {
    getCompanyGroup: (
      state,
      _action: PayloadAction<{
        companyId: string;
        groupId: string;
      }>
    ) => setLoading(state),
    getPremiseGroup: (
      state,
      _action: PayloadAction<{
        premiseId: string;
        groupId: string;
      }>
    ) => setLoading(state),
    getGroupSuccess: (state, action: PayloadAction<IGroup>) => {
      state.selectedGroup = action.payload;
      setSucceeded(state);
    },
    getGroupFailure: setFailed,
    getCompanyGroups: (state, _action: PayloadAction<string>) =>
      setLoading(state),
    getCompanyGroupsSuccess: (state, action: PayloadAction<IGroup[]>) => {
      state.companyGroups = action.payload;
      setSucceeded(state);
    },
    getCompanyGroupsFailure: setFailed,

    getPremiseGroups: (state, _action: PayloadAction<string>) =>
      setLoading(state),
    getPremiseGroupsSuccess: (state, action: PayloadAction<IGroup[]>) => {
      state.premiseGroups = action.payload;
      setSucceeded(state);
    },
    getPremiseGroupsFailure: setFailed,

    createCompanyGroup: (state, _action: PayloadAction<CreateGroupPayload>) =>
      setLoading(state),
    createPremiseGroup: (state, _action: PayloadAction<CreateGroupPayload>) =>
      setLoading(state),

    createCompanyGroupSuccess: (state, action: PayloadAction<IGroup>) => {
      state.companyGroups.push(action.payload);
      setSucceeded(state);
    },
    createPremiseGroupSuccess: (state, action: PayloadAction<IGroup>) => {
      state.companyGroups.push(action.payload);
      state.premiseGroups.push(action.payload);
      setSucceeded(state);
    },
    createGroupFailure: setFailed,

    updateCompanyGroup: (state, _action: PayloadAction<UpdateGroupPayload>) =>
      setLoading(state),
    updatePremiseGroup: (state, _action: PayloadAction<UpdateGroupPayload>) =>
      setLoading(state),

    updateGroupSuccess: (state, action: PayloadAction<IGroup>) => {
      const updated = action.payload;
      state.companyGroups = state.companyGroups.map((g) =>
        g._id === updated._id ? updated : g
      );
      state.premiseGroups = state.premiseGroups.map((g) =>
        g._id === updated._id ? updated : g
      );
      setSucceeded(state);
    },
    updateGroupFailure: setFailed,

    deleteCompanyGroup: (state, _action: PayloadAction<DeleteGroupPayload>) =>
      setLoading(state),
    deletePremiseGroup: (state, _action: PayloadAction<DeleteGroupPayload>) =>
      setLoading(state),

    deleteGroupSuccess: (state, action: PayloadAction<IGroup>) => {
      const deletedId = action.payload._id;
      state.companyGroups = state.companyGroups.filter(
        (g) => g._id !== deletedId
      );
      state.premiseGroups = state.premiseGroups.filter(
        (g) => g._id !== deletedId
      );
      setSucceeded(state);
    },
    deleteGroupFailure: setFailed,

    resetLoadingStatus: (state) => {
      state.loadingStatus = LoadingStatus.IDLE;
    },
  },
});

export const {
  getCompanyGroup,
  getPremiseGroup,
  getGroupSuccess,
  getGroupFailure,
  getCompanyGroups,
  getCompanyGroupsSuccess,
  getCompanyGroupsFailure,
  getPremiseGroups,
  getPremiseGroupsSuccess,
  getPremiseGroupsFailure,

  createCompanyGroup,
  createPremiseGroup,
  createCompanyGroupSuccess,
  createPremiseGroupSuccess,
  createGroupFailure,

  updateCompanyGroup,
  updatePremiseGroup,
  updateGroupSuccess,
  updateGroupFailure,

  deleteCompanyGroup,
  deletePremiseGroup,
  deleteGroupSuccess,
  deleteGroupFailure,

  resetLoadingStatus,
} = groupSlice.actions;

export const groupReducer = groupSlice.reducer;

/* Selectors */

const EMPTY_GROUPS: IGroup[] = [];

export const selectGroupsForAllPremisesOfCompany = (
  state: CustomerPortalState
) => state.group.companyGroups;

export const selectCompanyGroups = createSelector(
  selectGroupsForAllPremisesOfCompany,
  (groups) => groups.filter((group) => group.premise === undefined)
);

export const selectPremiseGroups = (state: CustomerPortalState) =>
  state.group.premiseGroups;

export const selectCompanyAndPremiseGroups = createSelector(
  selectCompanyGroups,
  selectPremiseGroups,
  (companyGroups, premiseGroups) => [...companyGroups, ...premiseGroups]
);

export const selectGroupsByNavigationLevel = createSelector(
  selectCompanyAndPremiseGroups,
  selectCurrentNavigationLevel,
  (groups, navigationLevel) => {
    if (navigationLevel === NavigationLevel.COMPANY)
      return groups.filter((group) => group.premise === undefined);
    else if (navigationLevel === NavigationLevel.PREMISE) return groups;
    return EMPTY_GROUPS;
  }
);

export const selectGroupsByNavigationLevelWithInheritance = createSelector(
  [
    selectCurrentNavigationLevel,
    selectNavigationScope,
    selectGroupsForAllPremisesOfCompany,
  ],
  (scopeLevel: NavigationLevel, scope: NavigationScope, groups: IGroup[]) => {
    if (scopeLevel === NavigationLevel.COMPANY) {
      return groups;
    }
    if (scopeLevel === NavigationLevel.PREMISE) {
      return groups.filter(
        (group) =>
          group.premise === undefined || group.premise === scope.premise
      );
    }

    return EMPTY_GROUPS;
  }
);

export const selectSelectedGroup = (state: CustomerPortalState) =>
  state.group.selectedGroup;

export const selectIsLoadingGroups = (state: CustomerPortalState) =>
  state.group.loadingStatus === LoadingStatus.LOADING;

/* Epics */

const getCompanyGroup$: CustomerPortalEpic = (action$) =>
  action$.pipe(
    filter(getCompanyGroup.match),
    switchMap(({ payload }) =>
      companyService.getGroup(payload.companyId, payload.groupId).pipe(
        map(getGroupSuccess),
        catchError((err) =>
          of(
            getCompanyGroupsFailure(),
            SetNotificationError(`Failed to fetch group: ${err.message}`)
          )
        )
      )
    )
  );

const getPremiseGroup$: CustomerPortalEpic = (action$) =>
  action$.pipe(
    filter(getPremiseGroup.match),
    switchMap(({ payload }) =>
      organizationUnitService.getGroup(payload.premiseId, payload.groupId).pipe(
        map(getGroupSuccess),
        catchError((err) =>
          of(
            getPremiseGroupsFailure(),
            SetNotificationError(`Failed to fetch group: ${err.message}`)
          )
        )
      )
    )
  );

const getCompanyGroups$: CustomerPortalEpic = (action$) =>
  action$.pipe(
    filter(getCompanyGroups.match),
    switchMap(({ payload: companyId }) =>
      companyService.getGroups(companyId).pipe(
        map(getCompanyGroupsSuccess),
        catchError((err) =>
          of(
            getCompanyGroupsFailure(),
            SetNotificationError(
              `Failed to load company groups: ${err.message}`
            )
          )
        )
      )
    )
  );

const getPremiseGroups$: CustomerPortalEpic = (action$) =>
  action$.pipe(
    filter(getPremiseGroups.match),
    switchMap(({ payload: premiseId }) =>
      organizationUnitService.getGroups(premiseId).pipe(
        map(getPremiseGroupsSuccess),
        catchError((err) =>
          of(
            getPremiseGroupsFailure(),
            SetNotificationError(
              `Failed to load premise groups: ${err.message}`
            )
          )
        )
      )
    )
  );

const createCompanyGroup$: CustomerPortalEpic = (action$) =>
  action$.pipe(
    filter(createCompanyGroup.match),
    switchMap(({ payload }) =>
      companyService
        .createGroup(payload.companyOrPremiseId, payload.group)
        .pipe(
          map(createCompanyGroupSuccess),
          catchError((err) =>
            of(
              createGroupFailure(),
              SetNotificationError(
                `Failed to create company group: ${err.message}`
              )
            )
          )
        )
    )
  );

const createPremiseGroup$: CustomerPortalEpic = (action$) =>
  action$.pipe(
    filter(createPremiseGroup.match),
    switchMap(({ payload }) =>
      organizationUnitService
        .createGroup(payload.companyOrPremiseId, payload.group)
        .pipe(
          map(createPremiseGroupSuccess),
          catchError((err) =>
            of(
              createGroupFailure(),
              SetNotificationError(
                `Failed to create premise group: ${err.message}`
              )
            )
          )
        )
    )
  );

const updateCompanyGroup$: CustomerPortalEpic = (action$) =>
  action$.pipe(
    filter(updateCompanyGroup.match),
    switchMap(({ payload }) =>
      companyService
        .updateGroup(payload.companyOrPremiseId, payload.id, payload.changes)
        .pipe(
          map(updateGroupSuccess),
          catchError((err) =>
            of(
              updateGroupFailure(),
              SetNotificationError(
                `Failed to update company group: ${err.message}`
              )
            )
          )
        )
    )
  );

const updatePremiseGroup$: CustomerPortalEpic = (action$) =>
  action$.pipe(
    filter(updatePremiseGroup.match),
    switchMap(({ payload }) =>
      organizationUnitService
        .updateGroup(payload.companyOrPremiseId, payload.id, payload.changes)
        .pipe(
          map(updateGroupSuccess),
          catchError((err) =>
            of(
              updateGroupFailure(),
              SetNotificationError(
                `Failed to update premise group: ${err.message}`
              )
            )
          )
        )
    )
  );

const deleteCompanyGroup$: CustomerPortalEpic = (action$) =>
  action$.pipe(
    filter(deleteCompanyGroup.match),
    switchMap(({ payload }) =>
      companyService.deleteGroup(payload.companyOrPremiseId, payload.id).pipe(
        map(deleteGroupSuccess),
        catchError((err) =>
          of(
            deleteGroupFailure(),
            SetNotificationError(
              `Failed to delete company group: ${err.message}`
            )
          )
        )
      )
    )
  );

const deletePremiseGroup$: CustomerPortalEpic = (action$) =>
  action$.pipe(
    filter(deletePremiseGroup.match),
    switchMap(({ payload }) =>
      organizationUnitService
        .deleteGroup(payload.companyOrPremiseId, payload.id)
        .pipe(
          map(deleteGroupSuccess),
          catchError((err) =>
            of(
              deleteGroupFailure(),
              SetNotificationError(
                `Failed to delete premise group: ${err.message}`
              )
            )
          )
        )
    )
  );

export const groupEpics = [
  getCompanyGroup$,
  getPremiseGroup$,
  getCompanyGroups$,
  getPremiseGroups$,
  createCompanyGroup$,
  createPremiseGroup$,
  updateCompanyGroup$,
  updatePremiseGroup$,
  deleteCompanyGroup$,
  deletePremiseGroup$,
];
